import Component from "../../lib/Component.js";
import ScrollInView from "./ScrollInView.js";
import {
  DEFAULTS,
  readContainerConfig,
  calculateRowGeometries,
  getScrollProgress,
  parseColor,
} from "./parallaxSeatsHelpers.js";

/**
 * Parallax theater seats effect — DOM-based implementation.
 *
 * Generates seat rows as HTML elements with CSS background-repeat,
 * gradient overlays, and blur filters for depth.
 *
 * @param {import("./parallaxSeatsHelpers.js").ParallaxSeatsOptions} options
 * @returns {Component}
 */
const ParallaxSeatsDom = ({
  containerSelector = DEFAULTS.containerSelector,
  rows = DEFAULTS.rows,
  frontRowWidth = DEFAULTS.frontRowWidth,
  perspectiveFactor = DEFAULTS.perspectiveFactor,
  parallaxFactor = DEFAULTS.parallaxFactor,
  fadeOpacityStep = DEFAULTS.fadeOpacityStep,
  responsiveBreakpoint = DEFAULTS.responsiveBreakpoint,
  responsiveSizeScale = DEFAULTS.responsiveSizeScale,
  parallaxPadding = DEFAULTS.parallaxPadding,
} = {}) => {
  const container = document.querySelector(containerSelector);
  if (!container) return Component.noop();

  /**
   * Get current size scale based on viewport width.
   */
  const getSizeScale = () => (window.innerWidth < responsiveBreakpoint ? responsiveSizeScale : 1);

  const { imgW, imgH, bgBase, bgFooter } = readContainerConfig(container);
  const geometries = calculateRowGeometries({
    rows,
    frontRowWidth,
    perspectiveFactor,
    parallaxFactor,
    fadeOpacityStep,
    imgW,
    imgH,
    bgBase,
    bgFooter,
  });

  const bgBaseRgb = parseColor(bgBase);
  const bgFooterRgb = parseColor(bgFooter);

  /**
   * Format an rgba() CSS string from an [r, g, b] array and alpha value.
   * @param {[number, number, number]} rgb
   * @param {number} alpha
   * @returns {string}
   */
  function rgba(rgb, alpha) {
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
  }

  // Add top gradient overlay to container
  const containerGradient = document.createElement("div");
  containerGradient.className = "parallax-seats-gradient";
  containerGradient.style.position = "absolute";
  containerGradient.style.top = `${-parallaxPadding}px`;
  containerGradient.style.left = "0";
  containerGradient.style.right = "0";
  containerGradient.style.height = `${parallaxPadding}px`;
  containerGradient.style.background = `linear-gradient(to bottom, ${rgba(bgBaseRgb, 0)} 0%, ${rgba(bgBaseRgb, 1)} 100%)`;
  containerGradient.style.pointerEvents = "none";
  containerGradient.style.zIndex = "14";
  container.appendChild(containerGradient);

  // Build DOM rows
  const rowElements = geometries.map((geo) => {
    const fadeRgb = geo.isFrontRow ? bgFooterRgb : bgBaseRgb;

    // Row wrapper
    const row = document.createElement("div");
    row.className = "seat-row";
    row.style.zIndex = geo.zIndex;
    if (geo.blur > 0) {
      row.style.filter = `blur(${geo.blur}px)`;
    }

    // Seat texture (repeating background)
    const inner = document.createElement("div");
    inner.className = "seat-row-inner";
    row.appendChild(inner);

    // Vertical fade overlay: transparent/tinted at top → solid at bottom
    const fade = document.createElement("div");
    fade.className = "seat-row-fade";
    fade.style.background = `linear-gradient(to bottom, ${rgba(fadeRgb, geo.fadeTopOpacity)} 0%, ${rgba(fadeRgb, 1)} 100%)`;
    row.appendChild(fade);

    // Horizontal side-fade overlay: dark edges → transparent center
    const sideFade = document.createElement("div");
    sideFade.className = "seat-row-side-fade";
    sideFade.style.background = `linear-gradient(to right, ${rgba(fadeRgb, geo.sideOpacity)} 0%, ${rgba(fadeRgb, 0)} 25%, ${rgba(fadeRgb, 0)} 75%, ${rgba(fadeRgb, geo.sideOpacity)} 100%)`;
    row.appendChild(sideFade);

    container.appendChild(row);

    return { el: row, inner, geo };
  });

  let currentProgress = 0;
  let currentScale = getSizeScale();

  /**
   * Update row sizes based on current scale.
   */
  function updateSizes() {
    const scale = getSizeScale();
    for (const { el, inner, geo } of rowElements) {
      el.style.height = `${geo.rowHeight * scale}px`;
      inner.style.backgroundSize = `${geo.seatWidth * scale}px auto`;
    }
  }

  /**
   * Update row positions based on scroll progress.
   */
  function updatePositions() {
    const scale = getSizeScale();
    for (const { el, geo } of rowElements) {
      if (geo.overlapStart != null) {
        const overlap = geo.overlapStart + (geo.overlapEnd - geo.overlapStart) * currentProgress;
        el.style.marginBottom = `${-geo.rowHeight * scale * overlap}px`;
      }
    }
  }

  // Scroll / resize handling
  let ticking = false;

  const update = () => {
    const newScale = getSizeScale();
    const scaleChanged = newScale !== currentScale;
    currentScale = newScale;

    if (scaleChanged) {
      updateSizes();
    }

    currentProgress = getScrollProgress(container, responsiveBreakpoint);
    updatePositions();
    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };

  return ScrollInView({
    componentName: "ParallaxSeatsDom",
    observables: [container],
    onEnter: () => {
      updateSizes(); // Set initial sizes
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      update();
    },
    options: {
      rootMargin: "200px 0px",
    },
  });
};

export default ParallaxSeatsDom;
